import type { ComponentPropsWithRef } from 'react'

import { cn } from '@gto/ui/cn'

import appStoreDownloadBadgeUrl from './assets/app-store-download-badge.svg?url'
import googlePlayDownloadBadgeUrl from './assets/google-play-download-badge.svg?url'
import type { AppStore } from './store-link'

const BADGE_URL_BY_STORE: Readonly<Record<AppStore, string>> = {
  app_store: appStoreDownloadBadgeUrl,
  google_play: googlePlayDownloadBadgeUrl,
}

/**
 * `medium` is the 40px height both stores ask for. `small` matches a `small`
 * Button, for the strip bar: a taller badge would grow the bar past the 48px
 * the app offsets its chrome by, and the bar would cover the top of the page.
 */
const SIZE_CLASS = {
  small: 'h-6',
  medium: 'h-10',
} as const

/**
 * The badge's accessible name when the CTA has no text of its own. The artwork
 * draws its words as paths, so without this the link would have no name at all.
 */
const STORE_BADGE_DEFAULT_LABEL: Readonly<Record<AppStore, string>> = {
  app_store: 'Download on the App Store',
  google_play: 'Get it on Google Play',
}

type StoreBadgeLinkProps = Omit<ComponentPropsWithRef<'a'>, 'children' | 'aria-label'> & {
  /**
   * The CTA's own text, used as the accessible name. Blank or whitespace falls
   * back to the store's name, so every caller passes its raw text.
   */
  label?: string
  store: AppStore
  /** Defaults to `medium`. */
  size?: keyof typeof SIZE_CLASS
}

/**
 * The store's official download badge ("Download on the App Store", "Get it on
 * Google Play"), as a link.
 *
 * The badge is an image, not a styled button: both stores' guidelines ask for
 * the official artwork unchanged, and inherited app typography would distort a
 * text version. The height is fixed per size and the width follows each
 * artwork's own ratio, so a badge never stretches.
 *
 * Presentation only. The carousel passes its `href`, analytics and hover
 * handlers through; the admin preview passes no `href`, so its badge is inert.
 */
export const StoreBadgeLink = ({
  label,
  store,
  size = 'medium',
  className,
  ...anchorProps
}: StoreBadgeLinkProps) => (
  <a
    {...anchorProps}
    aria-label={
      label !== undefined && label.trim() !== '' ? label : STORE_BADGE_DEFAULT_LABEL[store]
    }
    className={cn(
      'block shrink-0 rounded-md transition-[filter,box-shadow] duration-150 hover:shadow-glow-primary hover:brightness-125 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-main/70 active:brightness-95',
      SIZE_CLASS[size],
      className,
    )}
  >
    <img
      alt=""
      aria-hidden="true"
      className="block h-full w-auto"
      src={BADGE_URL_BY_STORE[store]}
    />
  </a>
)
